import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {Proposal} from '../../../model/proposal/proposal';
import {VersionFieldChange} from '../../../model/proposal/proposal-version-history-entry';

interface ParsedChange {
  dto: string;
  entityId: string | null;
  property: string;
}

/**
 * Read-only mirror of {@link import('../proposal-details-tab/proposal-details-expandable.component').ProposalDetailsExpandableComponent}
 * used inside the Version History tab. Each instance renders a specific historical
 * Proposal version supplied via [proposal] and decorates section headers with a
 * "Changed" badge when the corresponding fields appear in [changes].
 */
@Component({
    selector: 'app-proposal-version-details-expandable',
    templateUrl: './proposal-version-details-expandable.component.html',
    styleUrls: ['./proposal-version-details-expandable.component.scss', '../../../../styles/create-something.scss'],
    standalone: false
})
export class ProposalVersionDetailsExpandableComponent implements OnChanges {

  @Input() proposal: Proposal;
  @Input() changes: VersionFieldChange[] = [];
  @Input() existingFileIds: Set<string> = new Set<string>();

  /** Cached per render: which sections have at least one change. */
  changedSections: Record<string, boolean> = {};

  ngOnChanges(_changes: SimpleChanges): void {
    this.changedSections = this.computeChangedSections();
  }

  isChanged(section: string): boolean {
    return !!this.changedSections[section];
  }

  isPartnerEmpty(): boolean {
    const partner = this.proposal?.partner;
    if (!partner) return true;
    return (!partner.name && !partner.street && !partner.houseNumber
         && !partner.addressSupplement && !partner.postalCode
         && !partner.location && !partner.countryCode);
  }

  // ---- Internal: parse JaVers field strings and map them to UI sections ----

  private parseField(field: string): ParsedChange | null {
    if (!field || typeof field !== 'string') return null;
    const match = /(?:\.\.\.|^|\.)([A-Z]\w+)\/([^.]*)\.(.+)$/.exec(field);
    if (!match) return null;
    const [, dto, entityIdRaw, property] = match;
    return {
      dto,
      entityId: entityIdRaw && entityIdRaw.length > 0 ? entityIdRaw : null,
      property
    };
  }

  private mapSection(parsed: ParsedChange): string | null {
    switch (parsed.dto) {
      case 'PartnerDto':
        return 'partner';
      case 'LineOfBusiness':
        return 'risk';
      case 'RiskLocationDto':
        return 'riskLocations';
      case 'PreviousDamageDto':
        return 'previousDamages';
      case 'BinaryDataDto':
        return 'files';
      case 'ProposalDto':
      case 'ProposalInformationDto':
        return ProposalVersionDetailsExpandableComponent.proposalSection(parsed.property);
      default:
        return null;
    }
  }

  private static proposalSection(prop: string): string | null {
    if (prop === 'riskDescriptionFreeText') return 'riskDescription';
    if (prop === 'individualClausesFreeText' || prop === 'individualConditionsFreeText') return 'individualClausesConditions';
    if (prop === 'countriesWithLocalPolicies') return 'internationalProgram';
    if (['coveragePercentageRequested', 'insuredAmount', 'insuredAmountCurrency',
         'amountDescription', 'annualSalesAmount', 'annualSalesDescription', 'insuredSumType'].includes(prop)) {
      return 'sums';
    }
    if (['riskDateBegin', 'riskDateEnd', 'currency',
         'leadInsurerNeeded', 'followingInsurerNeeded',
         'mainDueDate', 'paymentMethod'].includes(prop)) {
      return 'risk';
    }
    if (prop === 'attachedFiles') return 'files';
    // Unknown / not user-visible properties don't decorate any section.
    return null;
  }

  private computeChangedSections(): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    if (!this.changes || this.changes.length === 0) {
      return result;
    }
    for (const change of this.changes) {
      const parsed = this.parseField(change?.field ?? '');
      if (!parsed) continue;
      const section = this.mapSection(parsed);
      if (section) {
        result[section] = true;
      }
    }
    return result;
  }
}
